import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';
import {
    Avatar,
    Drawer,
    Flex,
    HorizontalDivider,
    IconLogout,
    NavLink,
    SideNavLink,
    Stack,
} from '@myphx-engineering/phx-design-system';

import { useSidebarContext } from '../../contexts/SidebarProvider';
import { useAppMaintenanceStatus } from '../../hooks/use-app-maintenance-status';
import { useOptionalPatientContext } from '../../hooks/use-patient-context';
import { getAbsoluteRoute } from '../../util/get-absolute-route';
import { ConditionalComponent } from '../utility/ConditionalComponent';

import { HelpAndSettingsSection } from './sidebar/HelpAndSettingsSection';
import { MyAccountSection } from './sidebar/MyAccountSection';

// TODO: More menu items may be added/removed and routing needs to be added as the app evolves

export const AppSidebar = () => {
    const { close, isOpen } = useSidebarContext();
    const location = useLocation();
    const patientCtx = useOptionalPatientContext();
    const patient = patientCtx?.patient;

    const { disableAuthFlows, isAuthenticated, signIn } = useAuthContext();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isMaintenanceActive } = useAppMaintenanceStatus();

    const login = useCallback(
        () => signIn(location.pathname),
        [signIn, location.pathname]
    );
    const logout = useCallback(
        () => navigate(getAbsoluteRoute('logout')),
        [navigate]
    );

    useEffect(() => {
        /*
         * If the route changes, we should ensure that the sidebar is closed if
         * it isn't already. Note that there is not a check for `isOpen` here
         * due to conflicts that happen when the user tries to deliberately
         * open the sidebar.
         *
         * However, since we only trigger a close when the location changes, and
         * users aren't in the habit of navigating around in rapid succession,
         * there's no real performance issue calling `close` even if the sidebar
         * is already closed.
         */
        close();
    }, [location.pathname, location.key, close]);

    const drawerHeader = (
        // oxlint-disable-next-line jsx-a11y/aria-role -- tracked in docs/oxlint-violations.md
        <Flex gap="xs" role="header" align="center">
            <Avatar size="xl" initials={patient?.initials} />
            <ConditionalComponent condition={isAuthenticated}>
                <NavLink
                    onClick={() => navigate(getAbsoluteRoute('profile.root'))}
                    label={patient?.fullName}
                    variant="subtle"
                />
            </ConditionalComponent>
            <ConditionalComponent condition={!isAuthenticated}>
                <NavLink
                    disabled={isMaintenanceActive || disableAuthFlows}
                    onClick={login}
                    label={t('sidebar.button.signIn')}
                    variant="subtle"
                />
            </ConditionalComponent>
        </Flex>
    );

    return (
        <Drawer opened={isOpen} onClose={close} header={drawerHeader}>
            <Stack mih="100%" gap="xs" justify="space-between">
                <Stack gap="xs" flex={1}>
                    <MyAccountSection />
                    <HelpAndSettingsSection />

                    <ConditionalComponent condition={isAuthenticated}>
                        <HorizontalDivider variant="middle" />
                        <SideNavLink
                            label={t('sidebar.button.signOut')}
                            Icon={IconLogout}
                            onClick={logout}
                        />
                    </ConditionalComponent>
                </Stack>
            </Stack>
        </Drawer>
    );
};
